"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
}

// Extend Window interface to include Google Analytics property
declare global {
  interface Window {
    [key: string]: any; // This allows indexing with string
  }
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Essential cookies cannot be disabled
    analytics: true,
    functional: true,
  });

  useEffect(() => {
    // Check if user has already made their choice
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setIsVisible(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
    }
  }, []);

  const handleAcceptAll = () => {
    const allEnabled = {
      essential: true,
      analytics: true,
      functional: true,
    };
    savePreferences(allEnabled);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem("cookie-consent", JSON.stringify(prefs));
    setIsVisible(false);

    // Disable Google Analytics if analytics cookies are disabled
    if (!prefs.analytics && process.env.NEXT_PUBLIC_GA_ID) {
      window["ga-disable-" + process.env.NEXT_PUBLIC_GA_ID] = true;
    }

    // You can add more cookie management logic here
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg"
      data-oid="m8q:_z2"
    >
      <div className="container mx-auto p-4" data-oid="ph-v3wf">
        {!showPreferences ? (
          <div
            className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            data-oid="0t62b4y"
          >
            <div className="flex-grow" data-oid="u1zcz3l">
              <p className="text-gray-700 mb-2" data-oid="40q8c_4">
                We use cookies to enhance your experience on our website. By
                continuing to use this site, you agree to our use of cookies.
              </p>
              <Link
                href="/cookies"
                className="text-brand-blue hover:underline mr-4"
                data-oid="5ab3012"
              >
                Learn more
              </Link>
              <button
                onClick={() => setShowPreferences(true)}
                className="text-brand-blue hover:underline"
                data-oid="6jpm.6u"
              >
                Manage preferences
              </button>
            </div>
            <div className="flex gap-3" data-oid="1beiv-j">
              <button
                onClick={handleAcceptAll}
                className="bg-brand-pink text-white px-6 py-2 rounded-full hover:bg-opacity-90 transition-colors"
                data-oid="n3m:u-y"
              >
                Accept all
              </button>
              <button
                onClick={() => setIsVisible(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close cookie banner"
                data-oid="8fi5mt:"
              >
                <XMarkIcon className="h-6 w-6" data-oid="nw45dxg" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-oid="c28u9fh">
            <div
              className="flex justify-between items-center"
              data-oid="lncwq0f"
            >
              <h3 className="text-lg font-semibold" data-oid="m9n7nt3">
                Cookie Preferences
              </h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-gray-500 hover:text-gray-700"
                data-oid="ugije4-"
              >
                <XMarkIcon className="h-6 w-6" data-oid="wi9rj9j" />
              </button>
            </div>

            <div className="space-y-4" data-oid="akfvc_3">
              <div
                className="flex items-center justify-between"
                data-oid="3rxdcfc"
              >
                <div data-oid="96s2b5b">
                  <h4 className="font-medium" data-oid="4ne2kcp">
                    Essential Cookies
                  </h4>
                  <p className="text-sm text-gray-600" data-oid="h6hjedt">
                    Required for the website to function properly
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.essential}
                  disabled
                  className="h-4 w-4 text-brand-pink focus:ring-brand-pink border-gray-300 rounded"
                  data-oid="3sow4zj"
                />
              </div>

              <div
                className="flex items-center justify-between"
                data-oid="u.-qad5"
              >
                <div data-oid="nmst9y8">
                  <h4 className="font-medium" data-oid="suqa75n">
                    Analytics Cookies
                  </h4>
                  <p className="text-sm text-gray-600" data-oid="kf9z.z6">
                    Help us improve our website by collecting usage information
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) =>
                    setPreferences({
                      ...preferences,
                      analytics: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-brand-pink focus:ring-brand-pink border-gray-300 rounded"
                  data-oid="z01sipb"
                />
              </div>

              <div
                className="flex items-center justify-between"
                data-oid="svm.ocs"
              >
                <div data-oid="gq6yjmb">
                  <h4 className="font-medium" data-oid="k.cyc5l">
                    Functional Cookies
                  </h4>
                  <p className="text-sm text-gray-600" data-oid="_te7yyr">
                    Enable advanced features and personalization
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={(e) =>
                    setPreferences({
                      ...preferences,
                      functional: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-brand-pink focus:ring-brand-pink border-gray-300 rounded"
                  data-oid="d0u4_qj"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4" data-oid="w1o2jsr">
              <button
                onClick={handleSavePreferences}
                className="bg-brand-pink text-white px-6 py-2 rounded-full hover:bg-opacity-90 transition-colors"
                data-oid="rorc2df"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
